import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Header from '../components/Header';
import Footer from '../components/Footer';

const functions = getFunctions();

// How many times to re-try a "not_found" before giving up: LAN-mode kiosk
// sales sync to Firestore asynchronously via the POS, so a receipt tapped
// seconds after the SMS arrives may not exist yet.
const NOT_FOUND_RETRIES = 5;
const RETRY_DELAY_MS = 3000;

/**
 * Public 24h receipt — the deep-link target of the kiosk order-confirmation
 * SMS/email (/r/{businessId}/{saleId}). No login required; the unguessable
 * sale id in the URL is the capability, and getPublicReceipt enforces the
 * 24-hour window server-side (after that it only returns `expired`).
 */
const ReceiptPage = () => {
  const { businessId, saleId } = useParams();
  const [state, setState] = useState('loading'); // loading | ready | expired | not_found | error
  const [receipt, setReceipt] = useState(null);
  const retriesLeft = useRef(NOT_FOUND_RETRIES);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const load = async () => {
      try {
        const fn = httpsCallable(functions, 'getPublicReceipt');
        const res = await fn({ businessId, saleId });
        const data = res.data || {};
        if (cancelled) return;
        if (data.success && data.receipt) {
          setReceipt(data.receipt);
          setState('ready');
          return;
        }
        if (data.error === 'not_found' && retriesLeft.current > 0) {
          retriesLeft.current -= 1;
          timer = setTimeout(load, RETRY_DELAY_MS);
          return;
        }
        setState(data.error === 'expired' ? 'expired' : 'not_found');
      } catch (e) {
        console.error('[ReceiptPage] load error:', e);
        if (!cancelled) setState('error');
      }
    };

    if (businessId && saleId) {
      load();
    } else {
      setState('not_found');
    }
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [businessId, saleId]);

  const fmt = (v) => `$${(Number(v) || 0).toFixed(2)}`;
  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    } catch (_) {
      return '';
    }
  };

  const cardStyle = {
    maxWidth: 480,
    margin: '32px auto',
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    padding: '28px 24px',
    fontFamily: "'Poppins', Arial, sans-serif",
  };

  const renderBody = () => {
    if (state === 'loading') {
      return (
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <i className="fa fa-circle-notch fa-spin" style={{ fontSize: 32, color: '#f97316' }}></i>
          <p style={{ color: '#6b7280', marginTop: 16 }}>Loading your receipt…</p>
        </div>
      );
    }

    if (state === 'expired') {
      return (
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <i className="fa fa-clock" style={{ fontSize: 40, color: '#9ca3af' }}></i>
          <h2 style={{ color: '#1f2937', margin: '16px 0 8px' }}>This link has expired</h2>
          <p style={{ color: '#6b7280', margin: 0 }}>
            Receipt links are available for 24 hours after the order is placed.
          </p>
        </div>
      );
    }

    if (state === 'not_found' || state === 'error') {
      return (
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <i className="fa fa-receipt" style={{ fontSize: 40, color: '#9ca3af' }}></i>
          <h2 style={{ color: '#1f2937', margin: '16px 0 8px' }}>Receipt not available</h2>
          <p style={{ color: '#6b7280', margin: 0 }}>
            {state === 'error'
              ? 'Something went wrong loading this receipt. Please try again later.'
              : 'We couldn\'t find this receipt. If you just placed the order, wait a moment and reopen the link.'}
          </p>
        </div>
      );
    }

    const r = receipt;
    return (
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <h1 style={{ color: '#f97316', margin: 0, fontSize: 28 }}>FodyFee</h1>
          <p style={{ color: '#6b7280', margin: '4px 0 0', fontSize: 14 }}>{r.locationName}</p>
        </div>

        {r.guestNumber ? (
          <div style={{ background: '#1f2937', borderRadius: 12, padding: '16px', textAlign: 'center', marginBottom: 16 }}>
            <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>Your guest number — listen for it!</p>
            <p style={{ color: '#fff', fontWeight: 800, fontSize: 44, margin: '4px 0', letterSpacing: 2 }}>#{r.guestNumber}</p>
          </div>
        ) : null}

        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          {r.confirmationNumber ? (
            <p style={{ color: '#1f2937', fontWeight: 600, margin: '0 0 2px' }}>Order #{r.confirmationNumber}</p>
          ) : null}
          <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>{fmtDate(r.orderDate)}</p>
        </div>

        <hr style={{ border: 'none', borderTop: '1px dashed #d1d5db', margin: '12px 0' }} />

        {r.items.map((item, idx) => (
          <div key={idx} style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#1f2937', fontWeight: 600 }}>
              <span>{item.qty} x {item.productName}</span>
              <span>{fmt(item.total)}</span>
            </div>
            {item.options && item.options.length > 0 ? (
              <p style={{ color: '#6b7280', fontSize: 13, margin: '2px 0 0' }}>+ {item.options.join(', ')}</p>
            ) : null}
            {item.instructions ? (
              <p style={{ color: '#e65100', fontSize: 13, fontWeight: 600, margin: '2px 0 0' }}>Note: {item.instructions}</p>
            ) : null}
          </div>
        ))}

        <hr style={{ border: 'none', borderTop: '1px dashed #d1d5db', margin: '12px 0' }} />

        <div style={{ color: '#4b5563', fontSize: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span>Subtotal</span><span>{fmt(r.subtotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span>Tax</span><span>{fmt(r.tax)}</span>
          </div>
          {r.tip > 0 ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span>Tip</span><span>{fmt(r.tip)}</span>
            </div>
          ) : null}
          {r.convenienceFee > 0 ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span>Processing Fee</span><span>{fmt(r.convenienceFee)}</span>
            </div>
          ) : null}
          {r.discount > 0 ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, color: '#16a34a' }}>
              <span>Discount</span><span>-{fmt(r.discount)}</span>
            </div>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, color: '#1f2937', fontWeight: 700, fontSize: 18 }}>
            <span>Total</span><span>{fmt(r.total)}</span>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 16, color: '#6b7280', fontSize: 13 }}>
          <p style={{ margin: 0 }}>
            Paid with {r.paymentMethod === 'card' && r.cardBrand
              ? `${r.cardBrand} •••• ${r.cardLast4}`
              : (r.paymentMethod || 'card').toUpperCase()}
          </p>
          <p style={{ margin: '8px 0 0', color: '#9ca3af', fontSize: 12 }}>
            This link expires {fmtDate(r.expiresAt)}.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <div style={{ flex: 1, padding: '0 16px' }}>
        {renderBody()}
      </div>
      <Footer />
    </div>
  );
};

export default ReceiptPage;
